import { type FC, useEffect, useRef } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Backdrop, Paper, Popper, Portal, Typography } from '@mui/material'
import type { UUID } from '@/index'
import type { BlockingBucket, BlockingInboxEntry } from '../types'
import {
  BLOCKING_INBOX_BACKDROP_Z_INDEX,
  BLOCKING_INBOX_PANEL_Z_INDEX,
  BUCKET_HEADER_LABELS,
} from '../constants'
import { BlockingSessionTree } from './BlockingSessionTree'

const PANEL_TITLE_ID = 'inbox-panel-title'

interface BlockingInboxPanelProps {
  readonly bucket: BlockingBucket
  readonly anchorEl: HTMLElement | null
  readonly open: boolean
  readonly entries: readonly BlockingInboxEntry[]
  readonly submittingSessionIds: ReadonlySet<UUID>
  readonly onClose: (viaKeyboard?: boolean) => void
  readonly onSelect: (sessionId: UUID) => void
  readonly onAllowOnce: (entry: BlockingInboxEntry) => void
}

export const BlockingInboxPanel: FC<BlockingInboxPanelProps> = ({
  bucket,
  anchorEl,
  open,
  entries,
  submittingSessionIds,
  onClose,
  onSelect,
  onAllowOnce,
}) => {
  const isOpen = open && entries.length > 0
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose(true)
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Non-modal Popper: move focus into the panel on open so keyboard users reach
  // the cards immediately (focus returns to the triggering counter only on a
  // keyboard dismiss — see onClose).
  useEffect(() => {
    if (isOpen) panelRef.current?.focus()
  }, [isOpen])

  return (
    <>
      <Portal>
        <Backdrop open={isOpen} onClick={() => onClose()} sx={dimBackdropSx} />
      </Portal>
      <Popper open={isOpen} anchorEl={anchorEl} placement="bottom-end" sx={popperSx}>
        <Paper
          ref={panelRef}
          tabIndex={-1}
          sx={paperSx}
          role="dialog"
          aria-modal="false"
          aria-labelledby={PANEL_TITLE_ID}
        >
          <Typography id={PANEL_TITLE_ID} variant="subtitle2" sx={panelTitleSx}>
            {BUCKET_HEADER_LABELS[bucket]}
          </Typography>
          <BlockingSessionTree
            entries={entries}
            submittingSessionIds={submittingSessionIds}
            onSelect={onSelect}
            onAllowOnce={onAllowOnce}
          />
        </Paper>
      </Popper>
    </>
  )
}

const popperSx: SxProps<Theme> = {
  zIndex: BLOCKING_INBOX_PANEL_Z_INDEX,
}

const paperSx: SxProps<Theme> = {
  mt: 1,
  minWidth: 840,
  maxWidth: 1000,
  maxHeight: '70vh',
  overflowY: 'auto',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
}

const dimBackdropSx: SxProps<Theme> = {
  zIndex: BLOCKING_INBOX_BACKDROP_Z_INDEX,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
}

const panelTitleSx: SxProps<Theme> = {
  px: 1.5,
  pt: 1.5,
  pb: 0.5,
  fontSize: '1.05rem',
  fontWeight: 700,
  color: 'text.primary',
}
